(() => {
  // A fixed-height stage keeps normal document scrolling independent of the zoom.
  // Only the existing headline and its control transform; word rotation never restarts.
  const initScrollIntro = (root, reducedMotion) => {
    const frame = root.querySelector('.bioss-home-intro-v382__frame');
    const headline = root.querySelector('[data-bioss-scroll-title]') || root.querySelector('h1');
    const cue = root.querySelector('.bioss-home-intro-v382__explore');
    if (!frame || !headline || !cue || !CSS.supports('position', 'sticky')) return;
    const siteHeader = document.querySelector('.site-header');
    const searchPanel = document.querySelector('#bioss-home-search-panel');
    let distance = 0;
    let headerHeight = 0;
    let openingScale = 1;
    let pending = 0;
    let lastProgress = -1;
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const draw = () => {
      pending = 0;
      if (reducedMotion.matches) return;
      const progress = distance > 0 ? clamp((headerHeight - root.getBoundingClientRect().top) / distance, 0, 1) : 1;
      if (progress === lastProgress) return;
      lastProgress = progress;
      // Smooth endpoints with no time-based lag when the visitor reverses direction.
      const remaining = 1 - progress * progress * (3 - 2 * progress);
      root.style.setProperty('--bioss-intro-scale', String(1 + (openingScale - 1) * remaining));
      root.style.setProperty('--bioss-intro-title-y', `${distance * remaining / 2}px`);
      root.style.setProperty('--bioss-intro-control-y', `${distance * (1 - progress)}px`);
      root.style.setProperty('--bioss-intro-cue-opacity', String(Math.max(0, 1 - progress * 3)));
      cue.hidden = progress >= 0.34 || distance < 40;
    };
    const requestDraw = () => { if (!pending) pending = requestAnimationFrame(draw); };
    const measure = () => {
      lastProgress = -1;
      if (reducedMotion.matches) {
        root.classList.remove('is-scroll-intro');
        root.style.removeProperty('height');
        cue.hidden = true;
        return;
      }
      headerHeight = siteHeader ? siteHeader.getBoundingClientRect().height : 0;
      const stageStart = root.getBoundingClientRect().top + window.scrollY;
      const compactHeight = parseFloat(getComputedStyle(root).minHeight);
      // Keep the complete search field visible on arrival, without an automatic scroll.
      const searchForm = searchPanel && searchPanel.querySelector('form');
      const searchPreviewHeight = searchForm ? searchForm.getBoundingClientRect().height + parseFloat(getComputedStyle(searchPanel).paddingTop) + 24 : 160;
      const searchBreathingRoom = window.innerWidth <= 640 ? 80 : 100;
      const openingHeight = Math.max(compactHeight, window.innerHeight - stageStart - searchPreviewHeight - searchBreathingRoom);
      distance = openingHeight - compactHeight;
      root.style.height = `${openingHeight}px`;
      root.style.setProperty('--bioss-intro-compact-height', `${compactHeight}px`);
      root.style.setProperty('--bioss-intro-header-height', `${headerHeight}px`);
      root.classList.add('is-scroll-intro');
      // Reserve the widest rotating word, and scale the complete, unchanged title.
      const fontSize = parseFloat(getComputedStyle(headline).fontSize);
      const horizontalRoom = document.documentElement.clientWidth - (window.innerWidth <= 640 ? 32 : 96);
      openingScale = Math.max(1, Math.min(1.4, 96 / fontSize, horizontalRoom / headline.offsetWidth, (openingHeight - 144) / headline.offsetHeight));
      document.documentElement.style.setProperty('--bioss-home-scroll-offset', `${headerHeight + 24}px`);
      draw();
    };
    window.addEventListener('scroll', requestDraw, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('pageshow', measure);
    reducedMotion.addEventListener('change', measure);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(measure);
      if (siteHeader) observer.observe(siteHeader);
      const promo = document.querySelector('.promo-bar');
      if (promo) observer.observe(promo);
    }
    if (document.fonts) document.fonts.ready.then(measure);
    measure();
  };
  const init = () => {
    document.querySelectorAll('[data-bioss-rotating-headline]').forEach((root) => {
      if (root.dataset.headlineReady) return;
      root.dataset.headlineReady = 'true';
      const words = [...root.querySelectorAll('[data-bioss-headline-word]')];
      const toggle = root.querySelector('[data-bioss-headline-toggle]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      initScrollIntro(root, reducedMotion);
      if (words.length < 2 || !toggle) return;
      let index = 0;
      let paused = false;
      let visible = true;
      let timer;
      let cleanup;
      const updateControl = () => {
        toggle.hidden = reducedMotion.matches;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
        toggle.querySelector('[data-bioss-headline-pause]').toggleAttribute('hidden', paused);
        toggle.querySelector('[data-bioss-headline-play]').toggleAttribute('hidden', !paused);
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        updateControl();
        if (paused || reducedMotion.matches) return;
        timer = window.setInterval(() => {
          if (document.hidden || !visible) return;
          words.forEach((word) => word.classList.remove('is-leaving'));
          const previous = words[index];
          previous.classList.remove('is-active');
          previous.classList.add('is-leaving');
          index = (index + 1) % words.length;
          words[index].classList.add('is-active');
          window.clearTimeout(cleanup);
          cleanup = window.setTimeout(() => previous.classList.remove('is-leaving'), 650);
        }, 4200);
      };
      toggle.addEventListener('click', () => { paused = !paused; start(); });
      reducedMotion.addEventListener('change', () => {
        window.clearTimeout(cleanup);
        if (reducedMotion.matches) {
          index = 0;
          words.forEach((word, i) => {
            word.classList.remove('is-leaving');
            word.classList.toggle('is-active', i === 0);
          });
        }
        start();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }).observe(root);
      }
      window.addEventListener('pagehide', stop);
      window.addEventListener('pageshow', start);
      start();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
